import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import '../styles/UserManagement.css';
import usuariosApiService from '../services/usuariosApiService';

const EditUser = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  
  const [userData, setUserData] = useState({
    id: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    position: '',
    username: '',
    status: 'Activo',
    permissions: [],
    permissionsIds: [],
    lastLogin: '',
    createdAt: ''
  });

  const [isEditing, setIsEditing] = useState(false);
  const [originalData, setOriginalData] = useState({});
  const [loading, setLoading] = useState(true);
  const [permissionOptions, setPermissionOptions] = useState([]);

  // Cargar datos del usuario y permisos disponibles
  useEffect(() => {
    loadUserData();
    loadAvailablePermissions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadUserData = async () => {
    try {
      setLoading(true);
      const usuario = await usuariosApiService.getUsuarioById(id);
      
      // Separar nombre y apellido del nombreCompleto
      const nombreCompleto = usuario.nombreCompleto || '';
      const partes = nombreCompleto.trim().split(' ');
      const firstName = partes[0] || '';
      const lastName = partes.slice(1).join(' ') || '';
      
      const userData = {
        id: usuario.usuarioId,
        firstName: firstName,
        lastName: lastName,
        email: usuario.email || '',
        phone: usuario.telefono || '',
        position: '',
        username: usuario.username,
        status: usuario.estado || 'Activo',
        permissions: usuario.permisos || [],
        permissionsIds: usuario.permisosIds || [],
        lastLogin: usuario.lastLogin || '',
        createdAt: usuario.fechaCreacion || ''
      };
      
      setUserData(userData);
      setOriginalData(userData);
    } catch (error) {
      console.error('Error al cargar usuario:', error);
      alert('Error al cargar los datos del usuario');
      navigate('/user-management');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailablePermissions = async () => {
    try {
      const permisos = await usuariosApiService.getPermisosDisponibles();
      console.log('Permisos disponibles:', permisos);
      
      // Transformar permisos a formato esperado
      const permisosFormateados = permisos.map(p => ({
        id: p.id || p.PERMISOS_ID,
        label: p.nombre || p.PERMISOS_NOMBRE,
        description: getPermissionDescription(p.nombre || p.PERMISOS_NOMBRE)
      }));
      
      console.log('Permisos formateados:', permisosFormateados);
      setPermissionOptions(permisosFormateados);
    } catch (error) {
      console.error('Error al cargar permisos:', error);
      // Usar permisos por defecto si falla
      setPermissionOptions([]);
    }
  };

  const getPermissionDescription = (permissionName) => {
    const descriptions = {
      'CRUD Socias': 'Crear, leer, actualizar y eliminar información de socias',
      'CRUD Hospitales': 'Gestionar información de hospitales',
      'CRUD Inventario': 'Administrar el inventario de productos',
      'CRUD Categoría Inventario': 'Gestionar categorías de inventario',
      'CRUD Ubicación Inventario': 'Administrar ubicaciones de inventario',
      'CRUD Mentores': 'Gestionar mentores del sistema',
      'CRUD Usuarios': 'Administrar usuarios del sistema',
      'CRUD Tratamientos': 'Gestionar tratamientos médicos',
      'CRUD Médicos': 'Administrar información de médicos',
      'CRUD Profesiones': 'Gestionar profesiones',
      'CRUD Requisitos': 'Administrar requisitos de socias',
      'CRUD Características': 'Gestionar características del síndrome',
      'CRUD Formato Carnet': 'Administrar formatos de carnet',
      'CRUD Carnet': 'Gestionar carnets',
      'CRUD Especialidad Médico': 'Administrar especialidades médicas',
      'CRUD Tipo Socia': 'Gestionar tipos de socia',
      'CRUD Talla': 'Administrar tallas',
      'CRUD Reportes': 'Generar y gestionar reportes',
      'Administrador': 'Acceso completo a todas las funcionalidades del sistema'
    };
    return descriptions[permissionName] || 'Permiso del sistema';
  };

  const handlePermissionChange = (permissionId) => {
    setUserData(prev => {
      const isCurrentlySelected = prev.permissionsIds.includes(permissionId);
      const newPermissionsIds = isCurrentlySelected
        ? prev.permissionsIds.filter(p => p !== permissionId)
        : [...prev.permissionsIds, permissionId];
      
      // Actualizar también los nombres de permisos
      const newPermissions = permissionOptions
        .filter(opt => newPermissionsIds.includes(opt.id))
        .map(opt => opt.label);
      
      return {
        ...prev,
        permissionsIds: newPermissionsIds,
        permissions: newPermissions
      };
    });
  };

  const handleSave = async () => {
    try {
      // Solo guardar los permisos, el estado se maneja desde la tabla
      await usuariosApiService.actualizarUsuario(
        userData.id, 
        userData.status, 
        userData.permissionsIds
      );
      
      setIsEditing(false);
      setOriginalData({ ...userData });
      alert('Permisos actualizados exitosamente');
    } catch (error) {
      console.error('Error al actualizar usuario:', error);
      alert('Error al actualizar el usuario: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleCancel = () => {
    setUserData({ ...originalData });
    setIsEditing(false);
  };

  const handleBack = () => {
    navigate('/user-management');
  };

  return (
    <div className="edit-user-container">
      {loading ? (
        <div className="loading-container">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Cargando datos del usuario...</p>
        </div>
      ) : (
        <>
          <div className="edit-header">
            <button className="back-btn" onClick={handleBack}>
              <i className="fas fa-arrow-left"></i>
              Volver
            </button>
            <div className="header-info">
              <h1>Editar Usuario</h1>
              <p>Modifique la información y permisos del usuario</p>
            </div>
          </div>

      <div className="user-info-card">
        <div className="user-avatar-section">
          <div className="user-avatar-large">
            <i className="fas fa-user"></i>
          </div>
          <div className="user-basic-info">
            <h2>{userData.firstName} {userData.lastName}</h2>
            <p className="username">@{userData.username}</p>
            <div className="user-status">
              <span className={`status-badge ${userData.status.toLowerCase()}`}>
                {userData.status}
              </span>
            </div>
          </div>
        </div>

        <div className="user-meta-info">
          <div className="meta-item">
            <i className="fas fa-calendar-alt"></i>
            <span>Creado: {new Date(userData.createdAt).toLocaleDateString()}</span>
          </div>
          <div className="meta-item">
            <i className="fas fa-clock"></i>
            <span>Último acceso: {new Date(userData.lastLogin).toLocaleDateString()}</span>
          </div>
        </div>
      </div>

      <div className="edit-form-container">
        <div className="form-actions-header">
          {!isEditing ? (
            <button className="btn-primary" onClick={() => setIsEditing(true)}>
              <i className="fas fa-edit"></i>
              Editar Información
            </button>
          ) : (
            <div className="editing-actions">
              <button className="btn-secondary" onClick={handleCancel}>
                <i className="fas fa-times"></i>
                Cancelar
              </button>
              <button className="btn-primary" onClick={handleSave}>
                <i className="fas fa-save"></i>
                Guardar Cambios
              </button>
            </div>
          )}
        </div>

        <form className="edit-form">
          <div className="form-section">
            <h3>Permisos y Accesos</h3>
            
            <div className="permissions-section">
              {permissionOptions.map(permission => {
                const isChecked = userData.permissionsIds.includes(permission.id);
                return (
                  <div key={permission.id} className="permission-option">
                    <label className="permission-label">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handlePermissionChange(permission.id)}
                        disabled={!isEditing}
                      />
                      <span className="checkmark"></span>
                      <div className="permission-info">
                        <strong>{permission.label}</strong>
                        <p>{permission.description}</p>
                      </div>
                    </label>
                  </div>
                );
              })}
            </div>
          </div>
        </form>
      </div>
        </>
      )}
    </div>
  );
};

export default EditUser;
